import { nextTick } from "vue";
import type { FormErrorEvent, FormErrorWithId } from "@nuxt/ui";

// Certains widgets Nuxt UI (UInputNumber, UInputDate, UInputTime) appliquent l'id
// de la UFormField sur un container Reka qui n'est pas focusable. Dans ce cas
// on cherche un input/button descendant.
const FOCUSABLE_SELECTOR = [
  "input:not([type=hidden])",
  "select",
  "textarea",
  "button",
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(",");

function isInteractiveTag(el: HTMLElement): boolean {
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA" || tag === "BUTTON") return true;
  const tabindex = el.getAttribute("tabindex");
  return tabindex !== null && tabindex !== "-1";
}

interface ResolvedFieldError {
  err: FormErrorWithId;
  anchor: HTMLElement; // élément à amener dans le viewport
  focusable: HTMLElement | null; // élément à focuser (peut différer de l'ancre)
}

// Localise l'élément représentant un champ en erreur. On distingue l'ancre (élément
// VISIBLE à amener dans le viewport) du focusable (élément à focuser au clavier), car
// les deux diffèrent pour les groupes : `UFormFieldset` porte l'id sur le `<fieldset>`
// visible, mais ses CheckboxButton/RadioButton sont des `<input>` `sr-only` (1px, clippés).
// Scroller vers l'input sr-only ne déplace pas la fenêtre ; il faut scroller vers le fieldset.
function resolveFieldAnchor(id: string): Omit<ResolvedFieldError, "err"> | null {
  // Élément porteur de l'id : input direct, container Reka (UInputNumber/UInputDate), ou
  // `<fieldset>` d'un groupe. Toujours visible → bonne ancre de scroll. Le focusable peut
  // être un descendant (input du container Reka, ou première checkbox/radio sr-only).
  const direct = document.getElementById(id);
  if (direct) {
    const focusable = isInteractiveTag(direct) ? direct : direct.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    return { anchor: direct, focusable };
  }

  // Filet de sécurité si aucun élément ne porte l'id (groupe sans `UFormFieldset`) : l'id
  // ne vit alors que sur le message `${id}-error`, dont on remonte à la racine du UFormField.
  const errorEl = document.getElementById(`${id}-error`);
  const root = errorEl?.closest<HTMLElement>('[data-slot="root"]') ?? null;
  if (root) return { anchor: root, focusable: root.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) };
  if (errorEl) return { anchor: errorEl, focusable: null };
  return null;
}

// Zod renvoie les erreurs dans l'ordre du schema, pas du DOM. Sur les formulaires
// dont l'ordre des champs en template diffère de la déclaration du schema, le
// premier `errors[0]` peut sauter par-dessus un champ visuellement antérieur.
// On re-sélectionne l'erreur dont l'élément apparaît en premier en document order.
function pickFirstInDomOrder(errors: FormErrorWithId[]): ResolvedFieldError | undefined {
  const resolved = errors
    .map((err) => {
      const found = err.id ? resolveFieldAnchor(err.id) : null;
      return found ? { err, ...found } : null;
    })
    .filter((entry): entry is ResolvedFieldError => entry !== null);

  if (resolved.length === 0) return undefined;

  resolved.sort((a, b) => {
    const pos = a.anchor.compareDocumentPosition(b.anchor);
    if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
    if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1;
    return 0;
  });

  return resolved[0];
}

// Un champ en erreur peut vivre dans un conteneur replié (accordéon AppCollapsibleList,
// corps en v-show) : le scroll n'a aucun effet sur un élément sans boîte et le focus
// échoue. On remonte les ancêtres porteurs d'id et on clique tout bouton replié qui les
// contrôle (`aria-expanded="false"` + aria-controls) : c'est le composant lui-même qui
// s'ouvre, son état reste cohérent. Champ déjà visible → aucun bouton trouvé → no-op.
async function expandCollapsedAncestors(anchor: HTMLElement): Promise<void> {
  let expanded = false;
  for (let el: HTMLElement | null = anchor; el; el = el.parentElement) {
    if (!el.id) continue;
    const toggle = document.querySelector<HTMLElement>(`[aria-expanded="false"][aria-controls~="${CSS.escape(el.id)}"]`);
    if (!toggle) continue;
    toggle.click();
    expanded = true;
  }
  if (expanded) await nextTick();
}

// Pour les checkbox/radio sr-only, le `:focus-visible` n'est pas déclenché par un
// focus programmatique qui suit un clic souris (heuristique navigateur). On ajoute
// une classe sur l'élément focusé que CheckboxButton/RadioButton interprètent comme
// un focus visible, levée au prochain blur ou input.
const ERROR_FOCUS_CLASS = "form-error-focused";

function markErrorFocus(el: HTMLElement): void {
  el.classList.add(ERROR_FOCUS_CLASS);
  const cleanup = () => {
    el.classList.remove(ERROR_FOCUS_CLASS);
    el.removeEventListener("blur", cleanup);
    el.removeEventListener("input", cleanup);
    el.removeEventListener("change", cleanup);
  };
  el.addEventListener("blur", cleanup, { once: true });
  el.addEventListener("input", cleanup, { once: true });
  el.addEventListener("change", cleanup, { once: true });
}

// Cœur du scroll/focus, réutilisable hors de l'événement `@error` : les erreurs de
// validation serveur (422 Laravel) sont poussées via `setErrors()`, qui n'émet pas
// `@error`. AppForm rappelle cette fonction avec les erreurs résolues (porteuses d'`id`)
// pour amener le premier champ en erreur dans le viewport.
export async function scrollToFirstFieldError(errors: FormErrorWithId[]): Promise<void> {
  if (!errors.length) return;

  await nextTick();

  const first = pickFirstInDomOrder(errors);
  if (!first) return;

  const { anchor, focusable } = first;

  await expandCollapsedAncestors(anchor);

  // UForm utilise `loadingAuto: true` par défaut : pendant la validation, tous les inputs
  // passent en disabled. On planifie le focus au prochain frame pour que le disabled
  // ait été levé entretemps.
  if (focusable) {
    requestAnimationFrame(() => {
      try {
        focusable.focus({ preventScroll: true });
        markErrorFocus(focusable);
      } catch {
        // certaines implémentations rejettent focus() (élément détaché, etc.)
      }
    });
  }
  anchor.scrollIntoView({ behavior: "smooth", block: "center" });
}

export function focusFirstFormError(event: FormErrorEvent): Promise<void> {
  return scrollToFirstFieldError(event.errors ?? []);
}

// Option de select depuis une entrée GlobalData : `value` porte le code attendu par l'API.
export const toOption = (item: { code: string; label: string }) => ({ label: item.label, value: item.code });
